import React, { useState, useEffect } from 'react';

/**
 * OrdersTable - Reusable sortable table component for displaying orders
 *
 * Props:
 *   orders: Array of order objects to display
 *   theme: Theme object for colors, borderRadius, etc.
 *   t: Translation function
 *   onOrderClick: Click handler for when a row is clicked
 *   orderType: 'open' or 'closed' - determines default sort field
 */
const OrdersTable = ({ orders = [], theme, t, onOrderClick, orderType = 'closed' }) => {
    // Determine default sort based on order type
    const defaultSortField = orderType === 'open' ? 'created' : 'modified';
    const [sortField, setSortField] = useState(defaultSortField);
    const [sortDirection, setSortDirection] = useState('desc'); // Most recent first
    const [searchTerm, setSearchTerm] = useState('');

    // Reset sort to default whenever component renders or orderType changes
    useEffect(() => {
        setSortField(defaultSortField);
        setSortDirection('desc');
    }, [orderType, defaultSortField]);

    // Helper: Format currency with fallback
    const formatCurrency = (amount) => {
        return `$${Number(amount || 0).toFixed(2)}`;
    };

    // Helper: Format date/time for display (matches OrderSummaryCard pattern)
    const formatDateTime = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    // Helper: Calculate total (revenue + tip)
    const calculateTotal = (revenue, tip) => {
        return (parseFloat(revenue) || 0) + (parseFloat(tip) || 0);
    };

    // Helper: Determine if column should be shown
    const shouldShowColumn = (column) => {
        // Hide "Closed" and "Closed By" columns if all orders are open
        if ((column === 'closed' || column === 'closedBy') &&
            orders.every(order => order.closed === 0)) {
            return false;
        }
        return true;
    };

    // Sort handler - toggles direction on same column, changes field on new column
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if clicking same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Filter orders based on search term
    const filteredOrders = orders.filter(order => {
        if (!searchTerm.trim()) return true;

        const search = searchTerm.toLowerCase();

        // Search across all relevant fields
        const searchableFields = [
            String(order.id || ''),
            String(order.name || ''),
            String(order.table_name || ''),
            String(order.item_count || ''),
            String(order.order_discount || ''),
            String(order.tip || ''),
            String(calculateTotal(order.order_revenue, order.tip)),
            String(order.payment_type_name || ''),
            order.closed ? 'closed' : 'open',
            formatDateTime(order.created),
            String(order.open_user_name || ''),
            formatDateTime(order.closed_at || order.modified),
            String(order.close_user_name || '')
        ];

        return searchableFields.some(field =>
            field.toLowerCase().includes(search)
        );
    });

    // Sort orders based on current sort field and direction
    const sortedOrders = [...filteredOrders].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'id':
                aValue = parseInt(a.id) || 0;
                bValue = parseInt(b.id) || 0;
                break;
            case 'name':
                aValue = (a.name || '').toLowerCase();
                bValue = (b.name || '').toLowerCase();
                break;
            case 'table_name':
                aValue = (a.table_name || '').toLowerCase();
                bValue = (b.table_name || '').toLowerCase();
                break;
            case 'item_count':
                aValue = parseInt(a.item_count) || 0;
                bValue = parseInt(b.item_count) || 0;
                break;
            case 'order_discount':
                aValue = parseFloat(a.order_discount) || 0;
                bValue = parseFloat(b.order_discount) || 0;
                break;
            case 'tip':
                aValue = parseFloat(a.tip) || 0;
                bValue = parseFloat(b.tip) || 0;
                break;
            case 'total':
                aValue = calculateTotal(a.order_revenue, a.tip);
                bValue = calculateTotal(b.order_revenue, b.tip);
                break;
            case 'payment_type_name':
                aValue = (a.payment_type_name || '').toLowerCase();
                bValue = (b.payment_type_name || '').toLowerCase();
                break;
            case 'closed':
                aValue = a.closed ? 1 : 0;
                bValue = b.closed ? 1 : 0;
                break;
            case 'created':
                aValue = new Date(a.created || 0).getTime();
                bValue = new Date(b.created || 0).getTime();
                break;
            case 'open_user_name':
                aValue = (a.open_user_name || '').toLowerCase();
                bValue = (b.open_user_name || '').toLowerCase();
                break;
            case 'modified':
                aValue = new Date(a.closed_at || a.modified || 0).getTime();
                bValue = new Date(b.closed_at || b.modified || 0).getTime();
                break;
            case 'close_user_name':
                aValue = (a.close_user_name || '').toLowerCase();
                bValue = (b.close_user_name || '').toLowerCase();
                break;
            default:
                aValue = a[sortField];
                bValue = b[sortField];
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Render sort indicator
    const renderSortIndicator = (field) => {
        if (sortField === field) {
            return sortDirection === 'asc' ? ' ▲' : ' ▼';
        }
        return '';
    };

    // Common header cell style
    const headerStyle = {
        padding: '0.75rem',
        color: theme?.colors?.primary || '#1976d2',
        cursor: 'pointer',
        userSelect: 'none',
        borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`
    };

    if (orders.length === 0) {
        return (
            <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: theme?.colors?.textSecondary || '#666',
                fontSize: '0.95rem'
            }}>
                {t('noOrdersFound')}
            </div>
        );
    }

    return (
        <>
            {/* Search Input */}
            <div style={{ marginBottom: '1rem' }}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('searchOrders') || 'Search orders...'}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '0.95rem',
                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                        borderRadius: theme?.borderRadius || 4,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        outline: 'none'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = theme?.colors?.border || '#ddd';
                    }}
                />
                {searchTerm && (
                    <div style={{
                        marginTop: '0.5rem',
                        fontSize: '0.85rem',
                        color: theme?.colors?.textSecondary || '#666'
                    }}>
                        {t('showing')} {sortedOrders.length} {t('of')} {orders.length} {t('orders').toLowerCase()}
                    </div>
                )}
            </div>

            {/* No Results Message */}
            {sortedOrders.length === 0 && (
                <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: theme?.colors?.textSecondary || '#666',
                    fontSize: '0.95rem'
                }}>
                    {t('noResultsFound')} "{searchTerm}"
                </div>
            )}

            {/* Orders Table */}
            {sortedOrders.length > 0 && (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.9rem'
                    }}>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort('id')}
                                    style={{ ...headerStyle, textAlign: 'center' }}
                                >
                                    {t('orderNumber')}{renderSortIndicator('id')}
                                </th>
                                <th
                                    onClick={() => handleSort('name')}
                                    style={{ ...headerStyle, textAlign: 'left' }}
                                >
                                    {t('name')}{renderSortIndicator('name')}
                                </th>
                                <th
                                    onClick={() => handleSort('table_name')}
                                    style={{ ...headerStyle, textAlign: 'left' }}
                                >
                                    {t('tableName')}{renderSortIndicator('table_name')}
                                </th>
                                <th
                                    onClick={() => handleSort('item_count')}
                                    style={{ ...headerStyle, textAlign: 'center' }}
                                >
                                    {t('items')}{renderSortIndicator('item_count')}
                                </th>
                                <th
                                    onClick={() => handleSort('order_discount')}
                                    style={{ ...headerStyle, textAlign: 'right' }}
                                >
                                    {t('discount')}{renderSortIndicator('order_discount')}
                                </th>
                                <th
                                    onClick={() => handleSort('tip')}
                                    style={{ ...headerStyle, textAlign: 'right' }}
                                >
                                    {t('tip')}{renderSortIndicator('tip')}
                                </th>
                                <th
                                    onClick={() => handleSort('total')}
                                    style={{ ...headerStyle, textAlign: 'right' }}
                                >
                                    {t('total')}{renderSortIndicator('total')}
                                </th>
                                <th
                                    onClick={() => handleSort('payment_type_name')}
                                    style={{ ...headerStyle, textAlign: 'center' }}
                                >
                                    {t('payment')}{renderSortIndicator('payment_type_name')}
                                </th>
                                <th
                                    onClick={() => handleSort('closed')}
                                    style={{ ...headerStyle, textAlign: 'center' }}
                                >
                                    {t('status')}{renderSortIndicator('closed')}
                                </th>
                                <th
                                    onClick={() => handleSort('created')}
                                    style={{ ...headerStyle, textAlign: 'left' }}
                                >
                                    {t('opened')}{renderSortIndicator('created')}
                                </th>
                                <th
                                    onClick={() => handleSort('open_user_name')}
                                    style={{ ...headerStyle, textAlign: 'left' }}
                                >
                                    {t('openedBy')}{renderSortIndicator('open_user_name')}
                                </th>
                                {shouldShowColumn('closed') && (
                                    <th
                                        onClick={() => handleSort('modified')}
                                        style={{ ...headerStyle, textAlign: 'left' }}
                                    >
                                        {t('closed')}{renderSortIndicator('modified')}
                                    </th>
                                )}
                                {shouldShowColumn('closedBy') && (
                                    <th
                                        onClick={() => handleSort('close_user_name')}
                                        style={{ ...headerStyle, textAlign: 'left' }}
                                    >
                                        {t('closedBy')}{renderSortIndicator('close_user_name')}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedOrders.map(order => (
                                <tr
                                    key={order.id}
                                    onClick={() => onOrderClick(order)}
                                    style={{
                                        borderBottom: `1px solid ${theme?.colors?.primary || '#1976d2'}20`,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme?.colors?.primary ? `${theme.colors.primary}10` : '#1976d210';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <td style={{ padding: '0.75rem', textAlign: 'center', color: theme?.colors?.textSecondary || '#666', fontSize: '0.85rem' }}>
                                        {order.id}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>
                                        {order.name}
                                    </td>
                                    <td style={{ padding: '0.75rem' }}>
                                        {order.table_name || '-'}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        {order.item_count || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                        {formatCurrency(order.order_discount)}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                        {formatCurrency(order.tip)}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(calculateTotal(order.order_revenue, order.tip))}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        {order.payment_type_name || '-'}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <span style={{
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: theme?.borderRadius || 4,
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            backgroundColor: order.closed ? (theme?.colors?.success || '#4caf50') : (theme?.colors?.warning || '#ff9800'),
                                            color: theme?.colors?.surface || '#fff'
                                        }}>
                                            {order.closed ? t('closed') : t('open')}
                                        </span>
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                                        {formatDateTime(order.created)}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                                        {order.open_user_name || t('unknown')}
                                    </td>
                                    {shouldShowColumn('closed') && (
                                        <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                                            {order.closed ? formatDateTime(order.closed_at || order.modified) : '-'}
                                        </td>
                                    )}
                                    {shouldShowColumn('closedBy') && (
                                        <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                                            {order.closed ? (order.close_user_name || t('unknown')) : '-'}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default OrdersTable;
